export interface PersonNameIdentity {
    name?: string | null;
    preferredName?: string | null;
    givenName?: string | null;
    familyName?: string | null;
    sortName?: string | null;
}

export interface DerivedPersonNameIdentity {
    name: string;
    givenName?: string;
    familyName?: string;
    sortName?: string;
}

export type PersonNameIdentityUpdate = {
    name?: string;
    givenName?: string | undefined;
    familyName?: string | undefined;
    sortName?: string | undefined;
};

const IDENTITY_FIELDS = ['name', 'givenName', 'familyName', 'sortName'] as const;

function cleanNamePart(value: string | null | undefined): string | undefined {
    const cleaned = value?.replace(/\s+/g, ' ').trim();
    return cleaned ? cleaned : undefined;
}

function hasOwnField<T extends object>(value: T, field: keyof T): boolean {
    return Object.hasOwn(value, field);
}

function buildDisplayName(givenName: string | undefined, familyName: string | undefined): string | undefined {
    return [givenName, familyName].filter(Boolean).join(' ') || undefined;
}

function buildSortName(
    name: string | undefined,
    givenName: string | undefined,
    familyName: string | undefined
): string | undefined {
    if (familyName && givenName) {
        return `${familyName}, ${givenName}`;
    }

    return familyName || givenName || name;
}

function parseDisplayName(name: string | undefined): Pick<DerivedPersonNameIdentity, 'givenName' | 'familyName'> {
    if (!name) {
        return {};
    }

    const commaParts = name.split(',').map(cleanNamePart).filter(Boolean);
    if (commaParts.length >= 2) {
        return {
            familyName: commaParts[0],
            givenName: commaParts.slice(1).join(' '),
        };
    }

    const parts = name.split(/\s+/).map(cleanNamePart).filter(Boolean);
    if (parts.length <= 1) {
        return {
            givenName: parts[0],
        };
    }

    return {
        givenName: parts.slice(0, -1).join(' '),
        familyName: parts.at(-1),
    };
}

function shouldParseDisplayName(
    name: string | undefined,
    explicitGivenName: string | undefined,
    explicitFamilyName: string | undefined
): boolean {
    if (!name) {
        return false;
    }

    if (!explicitGivenName && explicitFamilyName && name === explicitFamilyName) {
        return false;
    }

    return true;
}

export function derivePersonNameIdentity(input: PersonNameIdentity): DerivedPersonNameIdentity {
    const explicitGivenName = cleanNamePart(input.givenName);
    const explicitFamilyName = cleanNamePart(input.familyName);
    const explicitName = cleanNamePart(input.name);
    const parsed = shouldParseDisplayName(explicitName, explicitGivenName, explicitFamilyName)
        ? parseDisplayName(explicitName)
        : {};
    const givenName = explicitGivenName ?? parsed.givenName;
    const familyName = explicitFamilyName ?? parsed.familyName;
    const name = explicitName ?? buildDisplayName(givenName, familyName) ?? '';
    const sortName = cleanNamePart(input.sortName) ?? buildSortName(name, givenName, familyName);

    return {
        name,
        ...(givenName ? { givenName } : {}),
        ...(familyName ? { familyName } : {}),
        ...(sortName ? { sortName } : {}),
    };
}

export function hasPersonNameIdentityUpdate(updates: Partial<PersonNameIdentity>): boolean {
    return IDENTITY_FIELDS.some(field => hasOwnField(updates, field));
}

export function derivePersonNameIdentityUpdate(
    current: PersonNameIdentity,
    updates: Partial<PersonNameIdentity>
): PersonNameIdentityUpdate {
    if (!hasPersonNameIdentityUpdate(updates)) {
        return {};
    }

    const nextName = hasOwnField(updates, 'name') ? updates.name : current.name;
    const nextGivenName = hasOwnField(updates, 'givenName')
        ? updates.givenName
        : hasOwnField(updates, 'name')
            ? undefined
            : current.givenName;
    const nextFamilyName = hasOwnField(updates, 'familyName')
        ? updates.familyName
        : hasOwnField(updates, 'name')
            ? undefined
            : current.familyName;
    const nextSortName = hasOwnField(updates, 'sortName') ? updates.sortName : undefined;

    const identity = derivePersonNameIdentity({
        name: nextName,
        givenName: nextGivenName,
        familyName: nextFamilyName,
        sortName: nextSortName,
    });

    return {
        name: identity.name,
        givenName: identity.givenName,
        familyName: identity.familyName,
        sortName: identity.sortName,
    };
}

export function getPersonDisplayName(person: PersonNameIdentity): string {
    return cleanNamePart(person.preferredName) ?? cleanNamePart(person.name) ?? '';
}

export function getPersonSortName(person: PersonNameIdentity): string {
    return cleanNamePart(person.sortName) ?? derivePersonNameIdentity(person).sortName ?? getPersonDisplayName(person);
}

export function comparePeopleBySortName(left: PersonNameIdentity, right: PersonNameIdentity): number {
    const primary = getPersonSortName(left).localeCompare(getPersonSortName(right), undefined, {
        sensitivity: 'base',
        numeric: true,
    });

    if (primary !== 0) {
        return primary;
    }

    return getPersonDisplayName(left).localeCompare(getPersonDisplayName(right), undefined, {
        sensitivity: 'base',
        numeric: true,
    });
}
